import { useEffect, useRef, useState } from "react";

interface StoryPage {
  index: number;
  title: string;
  bodyHtml: string;
  sourceUrl?: string;
}

interface Story {
  status: "pending" | "ready" | "failed" | "revising";
  ampUrl?: string;
  pages: StoryPage[];
  covers: Array<{ coverId: string; aspect: string; url: string }>;
}

interface Props {
  story: Story | null | undefined;
  onShare?: (target: { kind: "page" | "cover"; id: string | number }) => void;
}

/**
 * Story shell. Subscribes via useStory to api.story.queries.byEntity. Status
 * transitions pending → ready push reactively from Convex; no SSE/polling
 * code on the client (FR-007).
 *
 * Pages render full-bleed in a horizontal scroll-snap rail so users can swipe
 * natively on touch devices. Keyboard arrow keys scroll programmatically
 * AND update `pageIndex`; native scroll syncs `pageIndex` back via the
 * onScroll handler. Either input source stays in lockstep.
 */
export function StoryView({ story, onShare }: Props) {
  const [pageIndex, setPageIndex] = useState(0);
  const railRef = useRef<HTMLOListElement | null>(null);
  const lastScrollSync = useRef(0);

  useEffect(() => {
    function key(e: KeyboardEvent) {
      if (!story || story.status !== "ready") return;
      if (e.key === "ArrowRight")
        setPageIndex((p) => Math.min(p + 1, story.pages.length - 1));
      if (e.key === "ArrowLeft") setPageIndex((p) => Math.max(p - 1, 0));
    }
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [story]);

  // When pageIndex changes (keyboard / button), scroll the rail to that page.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const child = rail.children[pageIndex] as HTMLElement | undefined;
    if (!child) return;
    lastScrollSync.current = Date.now();
    if (typeof child.scrollIntoView === "function") {
      child.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    }
  }, [pageIndex]);

  if (!story || story.status === "pending") {
    return (
      <section
        className="story story-pending"
        lang="en"
        aria-busy="true"
        aria-live="polite"
        data-testid="story-pending"
      >
        <p>Building your Story…</p>
      </section>
    );
  }
  if (story.status === "failed") {
    return (
      <section className="story story-failed" lang="en" role="alert">
        <p>We could not build a Story for this answer.</p>
      </section>
    );
  }
  return (
    <section className="story story-ready" lang="en" data-testid="story-view">
      <ol
        className="story-pages"
        ref={railRef}
        aria-label={`Page ${pageIndex + 1} of ${story.pages.length}`}
        onScroll={(e) => {
          // Ignore scroll events fired as a side effect of our own scrollIntoView
          // for the next ~200ms.
          if (Date.now() - lastScrollSync.current < 200) return;
          const rail = e.currentTarget;
          const i = Math.round(rail.scrollLeft / rail.clientWidth);
          if (i !== pageIndex) setPageIndex(i);
        }}
      >
        {story.pages.map((p, i) => (
          <li
            key={p.index}
            data-story-page={p.index}
            tabIndex={i === pageIndex ? 0 : -1}
            aria-current={i === pageIndex ? "page" : undefined}
          >
            <h2>{p.title}</h2>
            <div dangerouslySetInnerHTML={{ __html: p.bodyHtml }} />
            {p.sourceUrl && (
              <a href={p.sourceUrl} target="_blank" rel="noreferrer noopener">
                Open original page
              </a>
            )}
          </li>
        ))}
      </ol>
      <nav className="story-controls" aria-label="Story navigation">
        <button
          type="button"
          disabled={pageIndex === 0}
          onClick={() => setPageIndex((p) => p - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span className="story-progress" aria-hidden="true">
          {pageIndex + 1} / {story.pages.length}
        </span>
        {story.ampUrl && (
          <a
            className="story-amp"
            href={story.ampUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            AMP
          </a>
        )}
        <button
          type="button"
          disabled={pageIndex === story.pages.length - 1}
          onClick={() => setPageIndex((p) => p + 1)}
          aria-label="Next page"
        >
          ›
        </button>
        {onShare && (
          <button
            type="button"
            className="share"
            onClick={() => onShare({ kind: "page", id: pageIndex })}
          >
            Share with my take
          </button>
        )}
      </nav>
    </section>
  );
}
